import React, { useState, Fragment, useMemo, useEffect } from "react";
import downloadIcon from "../../images/feed_images/download_icon.png"
import threeDotIcon from "../../images/feed_images/three_dots_icon.png"
import startIcon from "../../images/feed_images/star_icon.png"
import unionIcon from "../../images/feed_images/union_icon.png"
import dummyImage from "../../images/no-Image-dummy.png"
import { MediaItem } from "api/mediaApi";
import FeedDialog from "./FeedDialog";
import Masonry from 'react-masonry-css';
import { useFeed } from "contexts/FeedContext";

interface Props {
    feeds: MediaItem[];
    filters: any;
}

export interface DialogDetails {
    modelType: string;
    title: string;
    imgId: string;
    url: string;
    imgType: string;
    name: string;
    isInvalid: boolean;
}

export interface FeedDetails {
    id: string;
    url: string;
    type: string;
    name: string;
}

function MasonryGrid({ feeds, filters }: Props) {
    const [isDialogOpen, setDialogOpen] = useState(false);
    const [modelDetails, setModelDetails] = useState<DialogDetails>({ modelType: '', title: '', imgId: '', url: '', imgType: '', name: '', isInvalid: false });
    const { isLoadingFeeds } = useFeed();
    const showDialogModel = (modelType: string, title: string, feedDetails: FeedDetails, isInvalid: boolean) => {
        setModelDetails({
            modelType,
            title,
            imgId: feedDetails.id,
            url: feedDetails.url,
            imgType: feedDetails.type,
            name: feedDetails.name,
            isInvalid,
        })

        setDialogOpen(true)
    }

    const feedHeights = useMemo(() => {
        const min = 365;
        const max = 577;
        return feeds.map(() => {
            return `${Math.floor(Math.random() * (max - min + 1)) + min}px`;
        });
    }, [feeds]);

    useEffect(() => {
        if (isDialogOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }

        // Optional cleanup (handles unmounting)
        return () => {
            document.body.style.overflow = '';
        };
    }, [isDialogOpen]);

    const breakpointColumnsObj = {
        default: 5,
        1280: 4,
        1024: 3,
        768: 2,
        480: 1,
    };

    return (
        <Fragment>
            <Masonry
                breakpointCols={breakpointColumnsObj}
                className="my-masonry-grid"
                columnClassName="my-masonry-grid_column"
            >
                {feeds?.map((feed, index) => (
                    <div
                        key={feed.id || index}
                        className="break-inside-avoid mb-4 w-full md:min-w-[217px] group relative cursor-pointer rounded-3xl bg-[#EFEFEF] hover:brightness-[0.63]"
                        style={{ height: feedHeights[index] }}
                    >                  
                        {/* Image */}
                        <img
                            src={feed.url || dummyImage}
                            alt={feed.name}
                            className="h-full w-full object-contain"
                            onError={(e) => {
                                e.currentTarget.onerror = null;
                                e.currentTarget.src = dummyImage;
                            }}
                        />

                        {/* top icon */}
                        <div className="absolute top-[12px] md:right-[12px] right-[6px]">
                            {/* Vertex Button */}
                            <div
                                className="mb-[10px] bg-white w-[36px] h-[36px] rounded-full flex justify-center items-center"
                                onClick={() => {
                                    const isInvalid = !feed.url || feed.url.includes('default_hardlines');
                                    const title = isInvalid
                                        ? 'This image appears to be a placeholder. Are you sure you want to send it to Vertex AI?'
                                        : 'Do you want to send this item to Vertex AI?';
                                    showDialogModel('vertex', title, feed, isInvalid);
                                }}
                            >
                                <img src={startIcon} alt={startIcon} />
                            </div>

                            {/* Gemini Button */}
                            <div
                                className="bg-white w-[36px] h-[36px] rounded-full flex justify-center items-center"
                                onClick={() => {
                                    const isInvalid = !feed.url || feed.url.includes('default_hardlines');
                                    const title = isInvalid
                                        ? 'This image may not have valid content. Please confirm before Gemini analysis.'
                                        : 'Select a Prompt for Gemini Analysis:';
                                    showDialogModel('gemini', title, feed, isInvalid);
                                }}
                            >
                                <img src={unionIcon} alt={unionIcon} />
                            </div>
                        </div>


                        {/* bottom icon */}
                        <div className="absolute bottom-0 right-0 z-20">
                            <div className="flex items-center justify-end gap-[6px] md:m-[12px] m-[6px]">
                                <div className="bg-white w-[38px] h-[38px] rounded-full flex justify-center items-center">
                                    <img src={downloadIcon} alt={downloadIcon} />
                                </div>
                                <div className="bg-white w-[38px] h-[38px] rounded-full flex justify-center items-center">
                                    <img src={threeDotIcon} alt={threeDotIcon} />
                                </div>
                            </div>

                            {/* bottom */}
                            { feed.name && <div className="p-[10px] bg-black bg-opacity-50 overflow-hidden rounded-b-[24px]">
                                <p className="text-sm text-left text-white font-semibold">
                                    {feed.name}
                                </p>
                                <p className="text-xs text-left text-white pt-1">
                                    {feed.subCategory}
                                </p>
                            </div>}
                        </div>
                    </div>
                ))}
            </Masonry>
            {isLoadingFeeds && (
                <div className="text-center py-6">
                    <div className="loader border-4 border-gray-300 border-t-primaryColor rounded-full w-8 h-8 animate-spin mx-auto" />
                </div>
            )}
            {isDialogOpen && (<FeedDialog
                onClose={() => setDialogOpen(false)}
                modelDetails={modelDetails} />)
            }
        </Fragment>
    )
}
export default MasonryGrid;